import React from 'react';
import { Box, Card, Chip, Divider, Stack, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useTranslation } from 'react-i18next';
import SectionHeading from '../SectionHeading/SectionHeading';
import { TIERS, TIER_STYLES, tierLabel } from '../../tiers';

const BenefitItem = ({ label }) => (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <CheckIcon sx={{ color: 'primary.main', fontSize: 20, mt: '2px', flexShrink: 0 }} />
        <Typography variant="body2">{label}</Typography>
    </Stack>
);

const TierRewards = ({ data }) => {
    const { t } = useTranslation();
    const currentTierName = data && data.tier ? data.tier.name : null;

    return (
        <Box>
            <SectionHeading icon={AutoAwesomeIcon}>{t('loyalty.rewards.title')}</SectionHeading>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 3,
                }}
            >
                {TIERS.map((tier) => {
                    const isCurrent = tier.name === currentTierName;
                    const tierStyle = TIER_STYLES[tier.name];
                    return (
                        <Card
                            key={tier.name}
                            variant="outlined"
                            sx={{
                                // No `height: '100%'`: as a grid item it's a % of an auto row track,
                                // which the browser sizes from content-min and the card then overflows
                                // (~28px) into the next row → overlapping cards. Grid `align-items:
                                // stretch` already equalises card heights within a row.
                                display: 'flex',
                                flexDirection: 'column',
                                p: 3,
                                borderRadius: 3,
                                bgcolor: 'background.paper',
                                borderColor: isCurrent ? 'primary.main' : undefined,
                                borderWidth: isCurrent ? 2 : 1,
                            }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Chip
                                    size="small"
                                    icon={<WorkspacePremiumIcon />}
                                    label={tierLabel(t, tier.name)}
                                    sx={{
                                        ...tierStyle,
                                        fontWeight: 600,
                                        // Let a long tier pill ellipsise instead of pushing the
                                        // "Aktuell" chip out of the card.
                                        minWidth: 0,
                                        '& .MuiChip-icon': { color: 'inherit' },
                                    }}
                                />
                                {isCurrent && (
                                    <Chip
                                        size="small"
                                        color="primary"
                                        label={t('loyalty.rewards.current')}
                                        sx={{ ml: 'auto', flexShrink: 0, fontWeight: 600 }}
                                    />
                                )}
                            </Stack>

                            <Stack direction="row" spacing={0.5} sx={{ mt: 2.5, alignItems: 'baseline' }}>
                                <Typography variant="h3" component="span" sx={{ lineHeight: 1 }}>
                                    {tier.ratePercent}
                                </Typography>
                                <Typography variant="h5" component="span">
                                    %
                                </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                {t('loyalty.rewards.rateCaption')}
                            </Typography>

                            <Divider sx={{ my: 2.5 }} />

                            <Stack spacing={1.25}>
                                <BenefitItem label={t('loyalty.rewards.benefits.discounts')} />
                                {tier.ratePercent > 0 && (
                                    <BenefitItem
                                        label={t('loyalty.rewards.benefits.creditRate', {
                                            rate: tier.ratePercent,
                                        })}
                                    />
                                )}
                                <BenefitItem label={t('loyalty.rewards.benefits.upgrades')} />
                            </Stack>

                            <Box sx={{ mt: 'auto' }}>
                                <Divider sx={{ my: 2.5 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('loyalty.rewards.threshold', { points: tier.minPoints })}
                                </Typography>
                            </Box>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TierRewards;
